import type { WorkTask, WorkTaskEstimate, WorkTaskHourRange } from './work-task-types.js';

const invalid = (): never => {
  throw new Error('TASK_INVALID_INPUT');
};
function keys(value: unknown, expected: string[]): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return invalid();
  const row = value as Record<string, unknown>;
  if (Object.keys(row).length !== expected.length || expected.some(key => !Object.hasOwn(row, key))) return invalid();
  return row;
}
export function taskHours(value: unknown): number {
  if (
    typeof value !== 'number' ||
    !Number.isFinite(value) ||
    value < 0 ||
    value > 2000 ||
    Math.abs(value * 100 - Math.round(value * 100)) > 1e-8
  )
    return invalid();
  return value;
}
function range(value: unknown): WorkTaskHourRange {
  const row = keys(value, ['min', 'max']);
  const min = taskHours(row.min),
    max = taskHours(row.max);
  if (max < min) return invalid();
  return { min, max };
}
export function parseTaskEstimate(value: unknown): WorkTaskEstimate {
  if (!value || typeof value !== 'object') return invalid();
  const mode = (value as { mode?: unknown }).mode;
  if (mode === 'open') {
    keys(value, ['mode']);
    return { mode };
  }
  if (mode === 'total') {
    const row = keys(value, ['mode', 'total']);
    return { mode, total: range(row.total) };
  }
  if (mode === 'breakdown') {
    const row = keys(value, ['mode', 'research', 'development', 'testing']);
    return { mode, research: range(row.research), development: range(row.development), testing: range(row.testing) };
  }
  return invalid();
}
export function taskEstimate(task: Pick<WorkTask, 'estimate' | 'developmentHours' | 'testingHours'>): WorkTaskEstimate {
  if (task.estimate) return parseTaskEstimate(task.estimate);
  return {
    mode: 'breakdown',
    research: { min: 0, max: 0 },
    development: { min: task.developmentHours, max: task.developmentHours },
    testing: { min: task.testingHours, max: task.testingHours },
  };
}
export function estimateTotal(estimate: WorkTaskEstimate): WorkTaskHourRange | null {
  if (estimate.mode === 'open') return null;
  if (estimate.mode === 'total') return estimate.total;
  const parts = [estimate.research, estimate.development, estimate.testing];
  return {
    min: Math.round(parts.reduce((sum, item) => sum + item.min, 0) * 100) / 100,
    max: Math.round(parts.reduce((sum, item) => sum + item.max, 0) * 100) / 100,
  };
}
export function formatTaskHours(value: WorkTaskHourRange | null): string {
  if (!value) return 'Offen';
  const format = (n: number) => new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 }).format(n);
  return (value.min === value.max ? format(value.min) : `${format(value.min)}–${format(value.max)}`) + ' h';
}
export function compareWorkTasks(a: WorkTask, b: WorkTask): number {
  return (
    Number(b.pinned === true) - Number(a.pinned === true) ||
    b.updatedAt.localeCompare(a.updatedAt) ||
    a._id.localeCompare(b._id)
  );
}
